import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { LinearProgress } from '@material-ui/core';
import { EmojiEvents } from '@material-ui/icons';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { ITournament, TournamentType } from '@pvplevelup/core/dist/interfaces/Tournament/ITournament';
import { TournamentResultsData, TournamentResultsQuery, TournamentResultsVars, TournamentResultView } from '@pvplevelup/core/dist/queries/tournament/tournament';
import { PAGE_KIT } from 'components/Page/PageKit';
import { TOURNAMENT_ACCENT } from './tournamentFormat';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  champion: {
    display: 'grid',
    gridTemplateColumns: '56px minmax(0, 1fr)',
    gridColumnGap: 14,
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1.5),
    borderRadius: 14,
    background: 'radial-gradient(circle at 0% 0%, rgba(255, 179, 0, 0.3), transparent 60%), rgba(255, 179, 0, 0.06)',
    border: '1px solid rgba(255, 179, 0, 0.45)',
  },
  trophy: {
    width: 56,
    height: 56,
    borderRadius: 16,
    display: 'grid',
    placeItems: 'center',
    color: '#1a1300',
    background: `linear-gradient(135deg, #ffe082, ${TOURNAMENT_ACCENT})`,
    boxShadow: '0 8px 22px rgba(255, 179, 0, 0.3)',
  },
  kicker: {
    fontSize: '0.66rem',
    fontWeight: 900,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: TOURNAMENT_ACCENT,
  },
  championName: {
    fontSize: '1.3rem',
    fontWeight: 900,
    lineHeight: 1.2,
  },
  players: {
    marginTop: 2,
    fontSize: '0.8rem',
    color: PAGE_KIT.muted,
  },
  link: {
    cursor: 'pointer',
    '&:hover': {
      color: TOURNAMENT_ACCENT,
    },
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.86rem',
    '& th': {
      padding: '6px 8px',
      fontSize: '0.64rem',
      fontWeight: 900,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      textAlign: 'right',
      color: PAGE_KIT.faint,
    },
    '& td': {
      padding: '7px 8px',
      textAlign: 'right',
      borderTop: '1px solid rgba(255, 255, 255, 0.05)',
      fontVariantNumeric: 'tabular-nums',
    },
    '& th:nth-child(2), & td:nth-child(2)': {
      textAlign: 'left',
    },
  },
  place: {
    display: 'inline-grid',
    placeItems: 'center',
    minWidth: 30,
    height: 22,
    borderRadius: 7,
    fontSize: '0.74rem',
    fontWeight: 900,
    background: 'rgba(255, 255, 255, 0.07)',
  },
  entryName: {
    fontWeight: 800,
  },
}));

const MEDALS: Record<number, React.CSSProperties> = {
  1: {color: '#1a1300', background: `linear-gradient(135deg, #ffe082, ${TOURNAMENT_ACCENT})`},
  2: {color: '#15181c', background: 'linear-gradient(135deg, #eceff1, #90a4ae)'},
  3: {color: '#1c120b', background: 'linear-gradient(135deg, #ffcc80, #b26a3c)'},
};

interface Entry {
  key: string;
  name: string;
  place: number;
  players: TournamentResultView[];
  first: TournamentResultView;
}

/** A finished tournament's final placings, with the champion on top. Shared by the app and the website. */
export const TournamentStandings: React.FC<{tournament: ITournament, userPath?: string}> = ({tournament: t, userPath = '/users'}) => {
  const classes = useStyles();
  const history = useHistory();
  const { data, loading } = useQuery<TournamentResultsData, TournamentResultsVars>(TournamentResultsQuery, {variables: {tournament: String(t._id)}, fetchPolicy: 'cache-and-network'});
  const results = data?.tournamentResults || [];
  if(loading && results.length == 0)
    return <LinearProgress />;
  if(results.length == 0)
    return null;

  // one row per team (or duelist)
  const duel = t.type == TournamentType.Dueling;
  const entries: Entry[] = [];
  results.forEach((r) => {
    const key = duel ? String(r.user?._id) : String(r.team || r.user?._id);
    const found = entries.find((e) => e.key == key);
    if(found)
      found.players.push(r);
    else
      entries.push({key, name: (duel ? r.user?.displayname : r.teamName) || 'Unknown', place: r.place, players: [r], first: r});
  });
  entries.sort((a, b) => a.place - b.place || a.name.localeCompare(b.name));
  const champion = entries.find((e) => e.place == 1);
  const cup = t.type == TournamentType.PvE;
  const names = (e: Entry) => e.players.map((p, i) => (
    <React.Fragment key={String(p._id)}>
      {i > 0 && ', '}
      <span className={classes.link} onClick={() => p.user && history.push(`${userPath}/${p.user._id}`)}>{p.user?.displayname}</span>
    </React.Fragment>
  ));

  return (
    <div>
      {champion &&
        <div className={classes.champion}>
          <span className={classes.trophy}><EmojiEvents style={{fontSize: 32}} /></span>
          <div style={{minWidth: 0}}>
            <div className={classes.kicker}>Champion{duel ? '' : 's'}</div>
            <div className={classes.championName}>{champion.name}</div>
            {!duel && <div className={classes.players}>{names(champion)}</div>}
          </div>
        </div>
      }
      <table className={classes.table}>
        <thead>
          <tr>
            <th style={{textAlign: 'left', width: 44}}>#</th>
            <th>{duel ? 'Player' : 'Team'}</th>
            {cup ? <th>Points</th> : <React.Fragment><th>Series</th><th>Games</th></React.Fragment>}
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.key}>
              <td style={{textAlign: 'left'}}><span className={classes.place} style={MEDALS[e.place]}>{e.place}</span></td>
              <td>
                <span className={duel ? `${classes.entryName} ${classes.link}` : classes.entryName}
                  onClick={duel && e.first.user ? () => history.push(`${userPath}/${e.first.user!._id}`) : undefined}>{e.name}</span>
                {!duel && <div className={classes.players}>{names(e)}</div>}
              </td>
              {cup
                ? <td style={{fontWeight: 900, color: TOURNAMENT_ACCENT}}>{e.first.points ?? 0}</td>
                : <React.Fragment>
                    <td style={{fontWeight: 800}}>{e.first.seriesWon ?? 0}–{e.first.seriesLost ?? 0}</td>
                    <td style={{color: PAGE_KIT.muted}}>{e.first.gamesWon ?? 0}–{e.first.gamesLost ?? 0}</td>
                  </React.Fragment>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
